import { Col, Menu, Typography } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { semanticTheme } from '@tools/ui/dime';
import { TModules } from '@tools/ui/types';
import useT from '@tools/ui-hooks/useT';
import { moduleStore } from '@tools/ui-stores';
import { ColorModule } from '@tools/ui-utils/theme.util';
import { MenuModuleContact } from '../../MenuModuleContact';
import { RectangleModule, RectangleType } from '../RectangleModule';

const { Text } = Typography;

const ModulesContainer = styled.div`
  overflow: auto;
  max-height: 600px;
`;
const MenuContainer = styled(Menu)`
  width: 100%;
  border-inline-end: none !important;

  && .ant-menu-item {
    height: 40px;
    display: flex;
    padding: 12px;
    justify-content: flex-start;
    align-items: center;
    margin-top: 4px;
    padding-inline: 16px !important;
  }

  && .ant-menu-item-group-title {
    display: none;
  }

  && .ant-menu-item:hover {
    background-color: ${semanticTheme.dime.colors.surface.primaryHover} !important;
  }

  && .ant-menu-title-content {
    width: 100%;
  }

  && .ant-menu-item-selected {
    background-color: ${semanticTheme.dime.colors.surface.primarySelected} !important;
  }
`;

const AllAppsContainer = styled.div`
  margin-top: 10px;
`;

const AllAppsText = styled(Text)`
  margin-left: 8px;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 166.667% */
  letter-spacing: 0.24px;
  text-transform: uppercase;
`;

const NoModulesText = styled(Col)`
  font-weight: 600;
  font-size: 14px;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const mapModuleItem = (props: { menuModule: TModules; isFavorite: boolean; isActive: boolean }) => {
  return {
    key: props.menuModule.name,
    title: '',
    label: (
      <MenuModuleContact
        menuModule={props.menuModule}
        isFavorite={props.isFavorite}
        isActive={props.isActive}
      />
    ),
    icon: (
      <RectangleModule
        type={RectangleType.switcher}
        name={props.menuModule.name}
        color={props.menuModule.color as ColorModule}
      />
    ),
  };
};

const SwitchModules: React.FC<React.PropsWithChildren> = () => {
  const { t } = useT();
  const { otherModules, favoriteModules, activeModule } = moduleStore;
  const selectedMenu = activeModule?.name ? [activeModule.name] : [];
  const menuItems = React.useMemo(
    () =>
      otherModules?.map((menuModule) =>
        mapModuleItem({
          menuModule,
          isFavorite: false,
          isActive: activeModule?.path === menuModule.path,
        }),
      ),
    [otherModules, activeModule?.path],
  );

  const menuFavoriteItems = React.useMemo(
    () =>
      favoriteModules.map((menuModule) =>
        mapModuleItem({
          menuModule,
          isFavorite: true,
          isActive: activeModule?.path === menuModule.path,
        }),
      ),
    [favoriteModules, activeModule?.path],
  );

  return (
    <ModulesContainer>
      {!menuItems.length && !menuFavoriteItems.length ? (
        <NoModulesText>{t('sideContainer.noMoreModules')}</NoModulesText>
      ) : (
        <>
          {menuFavoriteItems.length ? (
            <>
              <AllAppsText>{t('sideContainer.favoritesApps')}</AllAppsText>
              <MenuContainer
                className="modules"
                mode="vertical"
                items={menuFavoriteItems}
                defaultSelectedKeys={selectedMenu}
              />
            </>
          ) : null}

          {menuItems.length ? (
            <AllAppsContainer>
              <AllAppsText>{t('sideContainer.allApps')}</AllAppsText>
              <MenuContainer
                mode="vertical"
                className="modules"
                items={menuItems}
                defaultSelectedKeys={selectedMenu}
              />
            </AllAppsContainer>
          ) : null}
        </>
      )}
    </ModulesContainer>
  );
};

export default SwitchModules;
